import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Viewer from "@/components/Viewer";
import { StatusBadge, Tag } from "@/components/badges";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PencilSimple, Trash, X, ArrowUp, ArrowDown, UserCircle, LinkSimple } from "@phosphor-icons/react";

const CATEGORIES = ["Publication", "Preprint", "News", "Dataset", "Report", "Blog", "Other"];

export default function EntryDetailDialog({ entryId, open, onOpenChange, onMutated }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", category: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const { data: entry } = useQuery({
    queryKey: ["entry-detail", entryId],
    queryFn: async () => (await api.get(`/entries/${entryId}`)).data,
    enabled: open && !!entryId,
  });

  useEffect(() => {
    if (entry) setForm({ title: entry.title || "", url: entry.url || "", category: entry.category || "", notes: entry.notes || "" });
    setEditing(false);
  }, [entry?.id]);

  if (!entry) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md bg-[#131722] border-white/10 text-slate-200">
          <DialogTitle className="sr-only">Entry details</DialogTitle>
          <DialogDescription className="sr-only">Loading entry.</DialogDescription>
          <p className="text-sm text-slate-400 py-6 text-center">Loading entry…</p>
        </DialogContent>
      </Dialog>
    );
  }

  const canEdit = user.role === "editor" || user.role === "admin" || entry.created_by === user.id;
  const canDelete = user.role === "editor" || user.role === "admin";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/entries/${entry.id}`, form);
      qc.setQueryData(["entry-detail", entry.id], data);
      toast.success("Entry updated");
      setEditing(false);
      onMutated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  const del = async () => {
    try {
      await api.delete(`/entries/${entry.id}`);
      toast.success("Entry permanently deleted");
      onOpenChange(false);
      onMutated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 gap-0 bg-[#0B0E14] border-white/10 text-slate-200 overflow-hidden" data-testid="entry-detail-dialog">
        <DialogTitle className="sr-only">Entry details</DialogTitle>
        <DialogDescription className="sr-only">Full view, edit and delete an entry.</DialogDescription>
        <div className="grid md:grid-cols-2 h-[72vh]">
          {/* Left: document viewer */}
          <div className="border-r border-white/5 min-w-0 bg-[#0B0E14]">
            <Viewer entry={entry} />
          </div>

          {/* Right: metadata / edit */}
          <div className="overflow-y-auto p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={entry.status} />
                {entry.category && <Tag kind="category">{entry.category}</Tag>}
              </div>
              <button data-testid="detail-close" onClick={() => onOpenChange(false)} className="text-slate-500 hover:text-slate-200 transition-colors duration-200"><X size={18} /></button>
            </div>

            {editing ? (
              <div className="space-y-4" data-testid="entry-edit-form">
                <div>
                  <Label className="text-slate-300">Title</Label>
                  <Input data-testid="edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-[#131722] border-white/10" />
                </div>
                <div>
                  <Label className="text-slate-300">URL</Label>
                  <Input data-testid="edit-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1.5 bg-[#131722] border-white/10" />
                </div>
                <div>
                  <Label className="text-slate-300">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="edit-category" className="mt-1.5 bg-[#131722] border-white/10"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{[...new Set([...CATEGORIES, ...(form.category ? [form.category] : [])])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Notes</Label>
                  <Textarea data-testid="edit-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 bg-[#131722] border-white/10" />
                </div>
                <div className="flex gap-2">
                  <Button data-testid="edit-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-full transition-colors duration-200">{saving ? "Saving…" : "Save changes"}</Button>
                  <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-full text-slate-400 hover:bg-white/5">Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold tracking-tight text-white" data-testid="detail-title">{entry.title}</h2>
                {entry.url && (
                  <a href={entry.url} target="_blank" rel="noreferrer" data-testid="detail-url" className="flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 break-all">
                    <LinkSimple size={14} /> {entry.url}
                  </a>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Meta label="Priority" value={entry.priority} kind="priority" />
                  <Meta label="Type" value={entry.entry_type} kind="type" />
                  <Meta label="Recommendation" value={entry.recommendation} kind="recommendation" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Votes</p>
                    <div className="flex items-center gap-3 mt-1 font-mono text-xs">
                      <span className="flex items-center gap-1 text-emerald-400"><ArrowUp size={13} weight="bold" />{entry.vote_up}</span>
                      <span className="flex items-center gap-1 text-red-400"><ArrowDown size={13} weight="bold" />{entry.vote_down}</span>
                    </div>
                  </div>
                </div>

                {entry.keywords?.length > 0 && (
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Keywords</p>
                    <div className="flex flex-wrap gap-1.5">{entry.keywords.map((k) => <Tag key={k} kind="keywords">{k}</Tag>)}</div>
                  </div>
                )}

                {entry.editor_notes && (
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Editorial Notes</p>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{entry.editor_notes}</p>
                  </div>
                )}

                <div className="flex items-center gap-2 text-xs text-slate-500 font-mono pt-1">
                  <UserCircle size={14} weight="duotone" /> Collected by {entry.created_by_name}
                  {entry.assigned_to_name && <span className="text-purple-400">· Assigned: {entry.assigned_to_name}</span>}
                </div>

                <div className="flex gap-2 pt-2 border-t border-white/5">
                  {canEdit && (
                    <Button data-testid="detail-edit-btn" onClick={() => setEditing(true)} className="gap-2 bg-blue-600 hover:bg-blue-500 rounded-full transition-colors duration-200">
                      <PencilSimple size={15} weight="duotone" /> Edit
                    </Button>
                  )}
                  {canDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" data-testid="detail-delete-btn" className="gap-2 rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors duration-200">
                          <Trash size={15} weight="duotone" /> Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#131722] border-white/10 text-slate-200">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            This permanently removes “{entry.title}” from all databases. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5">Cancel</AlertDialogCancel>
                          <AlertDialogAction data-testid="detail-delete-confirm" onClick={del} className="bg-red-600 hover:bg-red-500">Delete permanently</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, value, kind }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      {value ? <div className="mt-1"><Tag kind={kind}>{value}</Tag></div> : <p className="text-slate-600 text-sm mt-1">—</p>}
    </div>
  );
}
